"use client";

import { useSyncExternalStore } from "react";
import {
  ANALYTICS_LOCATION_CHANGE_EVENT,
  isAnalyticsSuppressedServerPath,
  isAnalyticsSuppressedUrl,
} from "@/lib/analytics/suppressed-paths";

// usePathname() covers path changes, while the head-time navigation barrier
// emits this event for query-only history writes (for example removing a token
// redirect from /signup without changing the pathname). This lets the root
// analytics components safely re-enable after leaving a suppressed URL without
// useSearchParams(), which would opt every page into client rendering.
function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, onStoreChange);
  return () =>
    window.removeEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, onStoreChange);
}

/**
 * Whether third-party analytics must be suppressed for the current URL.
 *
 * The SERVER snapshot (used for SSR and the hydration render) can't see the
 * query, so it fails CLOSED on the auth redirect landing pages that may carry a
 * token in `?redirect_url` — `useSyncExternalStore` guarantees the initial
 * client render matches this, so no loader ever runs with a stale "not
 * suppressed" value before the query has been inspected. The CLIENT snapshot,
 * used after hydration and on SPA navigations, inspects `window.location.search`
 * and makes the precise decision (re-enabling analytics on a token-free
 * `/login`/`/signup`).
 */
export function useAnalyticsSuppressed(pathname: string | null): boolean {
  return useSyncExternalStore(
    subscribe,
    () =>
      isAnalyticsSuppressedUrl(
        pathname,
        typeof window === "undefined" ? null : window.location.search,
      ),
    () => isAnalyticsSuppressedServerPath(pathname),
  );
}
